import { Link } from "react-router-dom";
import {
  FlaskConical,
  ShieldCheck,
  Globe2,
  HeartPulse,
  Microscope,
  ArrowUpRight,
  Pill,
  Stethoscope,
  Baby,
  ChevronRight,
} from "lucide-react";
import { COMPANY } from "@/lib/brand";

const VALUES = [
  {
    icon: Microscope,
    title: "Research-led",
    body: "A dedicated R&D centre developing safer, more accessible formulations across therapeutic areas.",
  },
  {
    icon: ShieldCheck,
    title: "Quality assured",
    body: "WHO-GMP compliant manufacturing with rigorous quality control at every batch.",
  },
  {
    icon: Globe2,
    title: "Globally available",
    body: "Trusted by clinicians and patients across emerging and established healthcare markets.",
  },
];

const AREAS = [
  {
    icon: HeartPulse,
    title: "Critical Care",
    blurb:
      "Injectable formulations for ICU and emergency settings, where availability matters most.",
    to: "/products",
  },
  {
    icon: Stethoscope,
    title: "Antibiotics",
    blurb:
      "Hospital-grade antibacterials for community and hospital-acquired infections.",
    to: "/products",
  },
  {
    icon: Pill,
    title: "Gastroenterology",
    blurb:
      "Injectables for acid-related, motility and post-operative GI conditions.",
    to: "/products",
  },
  {
    icon: Baby,
    title: "Anaesthesia",
    blurb:
      "Reliable anaesthesia and peri-operative products for surgical theatres.",
    to: "/products",
  },
];

const STATS = [
  { num: "6+", label: "Years building hospital partnerships" },
  { num: "27", label: "Formulations across hospital therapy areas" },
  { num: "2", label: "States served — Maharashtra & Madhya Pradesh" },
  { num: "100%", label: "Sourced from audited manufacturing partners" },
];

export default function Home() {
  return (
    <div data-testid="page-home" className="bg-[#F8F9F7]">
      {/* HERO */}
      <section
        data-testid="home-hero"
        className="relative overflow-hidden border-b border-[#E0E5E2]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-20 md:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7 anim-fade-up">
              <p className="overline text-[#1E8A2E]">
                Lenax Pharmaceuticals · Est. 2020
              </p>
              <h1 className="font-serif mt-6 text-5xl sm:text-6xl lg:text-[5.5rem] leading-[0.95] tracking-tight text-[#121413] font-medium">
                Leading you to
                <span className="block italic text-[#1E8A2E]">
                  better health.
                </span>
              </h1>
              <p className="mt-8 max-w-xl text-[#4A524D] text-base md:text-lg leading-relaxed">
                A hospital-focused pharmaceutical marketing company delivering
                quality injectable formulations across critical care,
                antibiotics, gastroenterology and anaesthesia — through a
                reliable distributor network in Maharashtra & Madhya Pradesh.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link
                  to="/products"
                  data-testid="hero-explore-products"
                  className="inline-flex items-center gap-2 bg-[#121413] text-white px-7 py-4 rounded-sm text-sm font-medium hover:bg-[#D92D2D] transition-colors duration-300"
                >
                  Explore our products
                  <ArrowUpRight size={16} />
                </Link>
                <Link
                  to="/about"
                  data-testid="hero-about-link"
                  className="inline-flex items-center gap-2 border border-[#121413]/20 text-[#121413] px-7 py-4 rounded-sm text-sm font-medium hover:border-[#1E8A2E] hover:text-[#1E8A2E] transition-colors duration-300"
                >
                  Our story
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 relative anim-fade">
              <div className="relative aspect-[4/5] overflow-hidden rounded-sm bg-[#121413]">
                <img
                  src="https://images.pexels.com/photos/9243372/pexels-photo-9243372.jpeg"
                  alt="Pharmaceutical researcher examining samples in a laboratory"
                  className="w-full h-full object-cover opacity-90"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#121413]/40 via-transparent to-transparent" />
              </div>
              <div className="hidden md:block absolute -left-6 -bottom-6 bg-white border border-[#E0E5E2] p-5 max-w-[16rem]">
                <p className="overline text-[#D92D2D]">Quality first</p>
                <p className="mt-2 font-serif text-xl text-[#121413] leading-tight">
                  Every batch tested. Every patient considered.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section
        data-testid="home-values"
        className="border-b border-[#E0E5E2] py-20 md:py-28"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
            <div className="md:col-span-4">
              <p className="overline text-[#1E8A2E]">Why Lenax</p>
              <h2 className="font-serif mt-4 text-4xl md:text-5xl tracking-tight text-[#121413]">
                Built on three quiet promises.
              </h2>
            </div>
            <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-8">
              {VALUES.map((v, i) => {
                const Icon = v.icon;
                return (
                  <div
                    key={v.title}
                    data-testid={`value-card-${i}`}
                    className="border-t border-[#121413] pt-6"
                  >
                    <Icon size={24} className="text-[#D92D2D]" />
                    <h3 className="font-serif text-2xl mt-5 text-[#121413]">
                      {v.title}
                    </h3>
                    <p className="mt-3 text-sm text-[#4A524D] leading-relaxed">
                      {v.body}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* THERAPEUTIC AREAS */}
      <section
        data-testid="home-areas"
        className="border-b border-[#E0E5E2] py-20 md:py-28 bg-white"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <p className="overline text-[#1E8A2E]">Therapeutic areas</p>
              <h2 className="font-serif mt-4 text-4xl md:text-5xl tracking-tight text-[#121413]">
                Care, across the spectrum.
              </h2>
            </div>
            <Link
              to="/products"
              data-testid="areas-view-all"
              className="inline-flex items-center gap-2 text-sm font-medium text-[#121413] hover:text-[#1E8A2E] transition-colors"
            >
              View full catalog <ChevronRight size={16} />
            </Link>
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E0E5E2] border border-[#E0E5E2]">
            {AREAS.map((a, i) => {
              const Icon = a.icon;
              return (
                <Link
                  key={a.title}
                  to={a.to}
                  data-testid={`area-card-${a.title.toLowerCase()}`}
                  className="group bg-white p-8 hover:bg-[#F1F3F0] transition-colors duration-300 flex flex-col min-h-[14rem]"
                >
                  <Icon
                    size={28}
                    className={
                      i % 2 === 0 ? "text-[#D92D2D]" : "text-[#1E8A2E]"
                    }
                  />
                  <h3 className="font-serif text-2xl mt-6 text-[#121413]">
                    {a.title}
                  </h3>
                  <p className="mt-3 text-sm text-[#4A524D] leading-relaxed">
                    {a.blurb}
                  </p>
                  <span className="mt-auto inline-flex items-center gap-1 text-xs font-medium text-[#121413] group-hover:text-[#1E8A2E] transition-colors">
                    Explore <ArrowUpRight size={14} />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* STATS — dark band */}
      <section
        data-testid="home-stats"
        className="bg-[#121413] text-white py-20 md:py-28 grain relative"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start">
            <div className="md:col-span-5">
              <p className="overline text-[#1E8A2E]">By the numbers</p>
              <h2 className="font-serif mt-4 text-4xl md:text-5xl leading-tight text-white">
                A quiet record of consistency.
              </h2>
              <p className="mt-6 text-white/70 max-w-md leading-relaxed">
                Over the years, Lenax has scaled its production, deepened its
                portfolio and reached patients in more places — while keeping
                quality at the centre of every decision.
              </p>
            </div>
            <div className="md:col-span-7 grid grid-cols-2 gap-px bg-white/10">
              {STATS.map((s, i) => (
                <div
                  key={i}
                  data-testid={`stat-${i}`}
                  className="bg-[#121413] p-8 md:p-10"
                >
                  <p className="font-serif text-5xl md:text-6xl text-white tracking-tight">
                    {s.num}
                  </p>
                  <p className="mt-3 text-sm text-white/70 leading-relaxed">
                    {s.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        data-testid="home-cta"
        className="py-20 md:py-28 border-b border-[#E0E5E2]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <p className="overline text-[#D92D2D]">Partner with Lenax</p>
              <h2 className="font-serif mt-4 text-4xl md:text-6xl leading-[1.05] tracking-tight text-[#121413]">
                Distribute with us. Build careers with us. Heal with us.
              </h2>
              <p className="mt-6 text-[#4A524D] max-w-2xl">
                Whether you are a distributor, healthcare professional or
                someone looking to build a meaningful career in life
                sciences — we'd love to hear from you.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <a
                  href={`mailto:${COMPANY.info_email}`}
                  data-testid="cta-contact"
                  className="inline-flex items-center gap-2 bg-[#1E8A2E] text-white px-7 py-4 rounded-sm text-sm font-medium hover:bg-[#19741f] transition-colors duration-300"
                >
                  Email our team
                </a>
                <Link
                  to="/career"
                  data-testid="cta-career"
                  className="inline-flex items-center gap-2 border border-[#121413] text-[#121413] px-7 py-4 rounded-sm text-sm font-medium hover:bg-[#121413] hover:text-white transition-colors duration-300"
                >
                  Explore careers
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5">
              <FlaskConical size={64} className="text-[#1E8A2E]" />
              <p className="mt-6 font-serif text-3xl text-[#121413] leading-tight">
                "Science only matters when it reaches the patient."
              </p>
              <p className="mt-3 overline text-[#7A827D]">— Our R&D Team</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
